import * as React from 'react';
import Icon from '@mdi/react';
import * as S from './tab-container.styles';

export interface ITab {
    id: string;
    label: string;
    icon?: string;
    count?: number;
}

interface ITabContainerBaseProps {
    tabs: ITab[];
    activeTab: string;
    onTabChange: (tabId: string) => void;
    /**
     * `folder` (default) is the filled bar that reads as the top edge of the panel below it.
     * `underline` is a flat row with a rule under the active tab, for tabs that FILTER one view
     * rather than switch between panels.
     */
    variant?: S.TabVariant;
    /**
     * Controls that act on the tabbed view — a search box, a filter — at the right end of the tab row.
     * They sit beside the tab list rather than inside it, so the `tablist` role holds only tabs, and
     * the row draws the rule under both. Without it the container lays out exactly as before.
     */
    trailing?: React.ReactNode;
}

/**
 * The panel is rendered EITHER here or by the caller, never both: two elements claiming the same id leaves
 * `aria-controls` resolving to whichever comes first, which is the kind of defect nobody sees. The union makes it a
 * compile error instead of a runtime surprise.
 */
export type ITabContainerProps = ITabContainerBaseProps &
    (
        | {
              /**
               * The id of the panel these tabs control, for a caller that renders the panel itself — a table card
               * below the tab row, say, whose content follows the active tab. Every tab then points at it with
               * `aria-controls`, and each tab gets an id (`tabElementId`) for the panel's `aria-labelledby`.
               */
              panelId: string;
              children?: never;
          }
        | {
              /**
               * The panel's content, when it can be a DOM child of the tab strip. This container then renders it as
               * the tabpanel, labelled by the active tab, and it fills the space under the row.
               */
              children: React.ReactNode;
              panelId?: never;
          }
        | { panelId?: undefined; children?: undefined }
    );

/** A tab's element id when its container has a `panelId` — what the panel's `aria-labelledby` names. */
export const tabElementId = (panelId: string, tabId: string): string => `${panelId}-tab-${tabId}`;

/** Namespaces the ids of a container that renders its own panel, so two strips on one page cannot collide. */
let instanceCount = 0;

export class TabContainer extends React.Component<ITabContainerProps> {
    private tabElements: Array<HTMLButtonElement | null> = [];

    private readonly ownPrefix = `tabs-${(instanceCount += 1)}`;

    /**
     * Arrow keys move between tabs and Home and End jump to either end — the WAI-ARIA tabs pattern. Only
     * the active tab is in the page's tab order, so without this a keyboard user could never reach the
     * others. Selection follows focus, which the pattern recommends when a tab's content shows at once.
     */
    private handleTabKeyDown = (e: React.KeyboardEvent): void => {
        const { tabs, activeTab, onTabChange } = this.props;
        const current = tabs.findIndex(tab => tab.id === activeTab);
        const last = tabs.length - 1;
        let next: number;

        switch (e.key) {
            case 'ArrowRight':
                next = current >= last ? 0 : current + 1;
                break;
            case 'ArrowLeft':
                next = current <= 0 ? last : current - 1;
                break;
            case 'Home':
                next = 0;
                break;
            case 'End':
                next = last;
                break;
            default:
                return;
        }

        const target = tabs[next];
        if (!target) {
            return;
        }
        e.preventDefault();
        this.tabElements[next]?.focus();
        if (target.id !== activeTab) {
            onTabChange(target.id);
        }
    };

    private handleTabClick = (tabId: string) => (e: React.MouseEvent): void => {
        e.preventDefault();
        if (tabId !== this.props.activeTab) {
            this.props.onTabChange(tabId);
        }
    };

    public render(): React.ReactElement {
        const { activeTab, children, trailing, variant = 'folder' } = this.props;

        return (
            <S.TabContainerWrapper fills={Boolean(children)}>
                {/* One structure whether or not there are trailing controls. A caller that shows and hides them — the
                    denials toolbar leaves with its table — would otherwise swap the tree under the tabs, remounting
                    them and dropping focus from the tab the keyboard is on. */}
                <S.TabBar variant={variant}>
                    {this.renderTabList()}
                    {trailing && <S.TabTrailing>{trailing}</S.TabTrailing>}
                </S.TabBar>
                {children && (
                    <S.TabContent
                        role="tabpanel"
                        id={this.ownPanelId()}
                        aria-labelledby={this.tabDomId(activeTab)}>
                        {children}
                    </S.TabContent>
                )}
            </S.TabContainerWrapper>
        );
    }

    /** The panel this container renders itself, namespaced per instance so two strips on a page cannot collide. */
    private ownPanelId(): string {
        return `${this.ownPrefix}-panel`;
    }

    /**
     * A tab's element id, for the panel's `aria-labelledby`. With `panelId` the caller can compute it too, through
     * the exported `tabElementId`; without one the ids belong to this instance alone.
     */
    private tabDomId(tabId: string): string {
        const { panelId } = this.props;

        return panelId ? tabElementId(panelId, tabId) : `${this.ownPrefix}-tab-${tabId}`;
    }

    /**
     * The panel a tab controls — emitted only where one exists. A reference to a missing element is a broken
     * relationship for assistive technology, and every tab in a label-only strip used to carry one.
     */
    private controlledPanelId(isActive: boolean): string | undefined {
        const { panelId, children } = this.props;

        if (panelId) {
            return panelId;
        }
        // Without a panelId this container renders the active tab's panel, and only that one.
        return children && isActive ? this.ownPanelId() : undefined;
    }

    private renderTabList(): React.ReactElement {
        const { tabs, activeTab, panelId, variant = 'folder' } = this.props;

        return (
            <S.TabList role="tablist" variant={variant}>
                {tabs.map((tab, index) => {
                    const isActive = tab.id === activeTab;
                    const zIndex = isActive ? tabs.length : tabs.length - index;
                    const isLastTab = index === tabs.length - 1;
                    // The divider separates adjacent folder tabs, which share a filled bar.
                    // Underline tabs are separated by whitespace and a rule already runs beneath
                    // them, so a divider reads as a stray pipe.
                    const showDivider = variant === 'folder' && !isLastTab && !isActive;

                    return (
                        <React.Fragment key={tab.id}>
                            <S.Tab
                                ref={(element: HTMLButtonElement | null): void => {
                                    this.tabElements[index] = element;
                                }}
                                role="tab"
                                id={this.tabDomId(tab.id)}
                                aria-selected={isActive}
                                aria-controls={this.controlledPanelId(isActive)}
                                tabIndex={isActive ? 0 : -1}
                                active={isActive}
                                zIndex={zIndex}
                                variant={variant}
                                onClick={this.handleTabClick(tab.id)}
                                onKeyDown={this.handleTabKeyDown}
                            >
                                {tab.icon && (
                                    <S.TabIcon active={isActive}>
                                        <Icon path={tab.icon} size={0.67} />
                                    </S.TabIcon>
                                )}
                                <S.TabLabel active={isActive}>{tab.label}</S.TabLabel>
                                {tab.count !== undefined && <S.TabCount active={isActive}>{tab.count}</S.TabCount>}
                            </S.Tab>
                            {showDivider && <S.TabDivider />}
                        </React.Fragment>
                    );
                })}
            </S.TabList>
        );
    }
}
